import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import {
  Receipt,
  ReceiptVm,
  ReceiptListResponse,
  CustomerInvoice,
  CustomerAdvanceForReceipt,
  ReceiptAccount,
  ApiResponse,
} from '@/api/types'

/**
 * Sales Receipts management service
 * Handles all receipt-related API operations
 */
export class ReceiptsService {
  /**
   * Get paginated list of receipts for a branch
   */
  async getAllReceipts(
    branchId: number,
    pageNumber: number,
    pageSize: number
  ): Promise<ReceiptListResponse> {
    const url = endpoints.receipts.getAll(branchId, pageNumber, pageSize)
    const response = await apiClient.get<ApiResponse<ReceiptListResponse> | ReceiptListResponse | any>(url)
    const payload = response.data

    // Helper to map receipts array
    const mapReceipts = (receipts: any[]): Receipt[] => {
      return receipts.map(rec => this.mapReceiptData(rec))
    }

    if (payload && typeof payload === 'object') {
      if ('success' in payload && 'data' in payload) {
        const data = payload.data
        if (Array.isArray(data)) {
          return {
            totalRecords: payload.totalRecords ?? data.length,
            pageNumber: payload.pageNumber ?? pageNumber,
            pageSize: payload.pageSize ?? pageSize,
            data: mapReceipts(data),
          }
        }
        return data as ReceiptListResponse
      }

      if ('data' in payload && Array.isArray(payload.data)) {
        return {
          totalRecords: payload.totalRecords ?? payload.data.length,
          pageNumber: payload.pageNumber ?? pageNumber,
          pageSize: payload.pageSize ?? pageSize,
          data: mapReceipts(payload.data),
        }
      }
    }

    if (Array.isArray(payload)) {
      return {
        totalRecords: payload.length,
        pageNumber,
        pageSize,
        data: mapReceipts(payload),
      }
    }

    return {
      totalRecords: payload?.totalRecords ?? 0,
      pageNumber: payload?.pageNumber ?? pageNumber,
      pageSize: payload?.pageSize ?? pageSize,
      data: payload?.data ? mapReceipts(payload.data) : [],
    }
  }

  /**
   * Get receipt by ID
   */
  async getReceiptById(branchId: number, id: number): Promise<Receipt> {
    const url = endpoints.receipts.getById(branchId, id)
    const response = await apiClient.get<ApiResponse<Receipt> | Receipt | any>(url)
    
    let receiptData: any
    
    if ('data' in response.data && 'success' in response.data) {
      receiptData = response.data.data
    } else if ('data' in response.data) {
      receiptData = response.data.data
    } else {
      receiptData = response.data
    }
    
    return this.mapReceiptData(receiptData)
  }

  /**
   * Get customer invoices
   */
  async getCustomerInvoices(customerId: number): Promise<CustomerInvoice[]> {
    const url = endpoints.receipts.getCustomerInvoices(customerId)
    const response = await apiClient.get<ApiResponse<CustomerInvoice[]> | CustomerInvoice[] | any>(url)
    
    if (Array.isArray(response.data)) {
      return response.data
    }
    if ('data' in response.data && Array.isArray(response.data.data)) {
      return response.data.data
    }
    return []
  }

  /**
   * Get customer advances
   * @param customerId - Customer ID
   * @param receiptId - Receipt ID (0 for new receipt, actual receiptId for edit mode)
   */
  async getCustomerAdvances(customerId: number, receiptId: number = 0): Promise<CustomerAdvanceForReceipt[]> {
    const url = endpoints.receipts.getCustomerAdvances(customerId, receiptId)
    const response = await apiClient.get<ApiResponse<CustomerAdvanceForReceipt[]> | CustomerAdvanceForReceipt[] | any>(url)
    
    if (Array.isArray(response.data)) {
      return response.data
    }
    if ('data' in response.data && Array.isArray(response.data.data)) {
      return response.data.data
    }
    return []
  }

  /**
   * Get customer advance lines by receipt ID
   */
  async getCustomerAdvanceLinesByReceiptId(receiptId: number): Promise<any[]> {
    const url = endpoints.receipts.getCustomerAdvanceLinesByReceiptId(receiptId)
    const response = await apiClient.get<ApiResponse<any[]> | any[] | any>(url)
    
    if (Array.isArray(response.data)) {
      return response.data
    }
    if ('data' in response.data && Array.isArray(response.data.data)) {
      return response.data.data
    }
    return []
  }

  /**
   * Get receipt lines by receipt ID
   * Returns array of receipt line items containing invoice IDs
   */
  async getReceiptLinesByReceiptId(receiptId: number): Promise<any[]> {
    const url = endpoints.receipts.getReceiptLinesByReceiptId(receiptId)
    const response = await apiClient.get<ApiResponse<any[]> | any[] | any>(url)
    
    if (Array.isArray(response.data)) {
      return response.data
    }
    if ('data' in response.data && Array.isArray(response.data.data)) {
      return response.data.data
    }
    if ('success' in response.data && 'data' in response.data && Array.isArray(response.data.data)) {
      return response.data.data
    }
    return []
  }

  /**
   * Get all accounts for a branch
   */
  async getAllAccounts(branchId: number): Promise<ReceiptAccount[]> {
    const url = endpoints.receipts.getAllAccounts(branchId)
    const response = await apiClient.get<ApiResponse<ReceiptAccount[]> | ReceiptAccount[] | any>(url)
    
    if (Array.isArray(response.data)) {
      return response.data
    }
    if ('data' in response.data && Array.isArray(response.data.data)) {
      return response.data.data
    }
    return []
  }

  /**
   * Insert receipt
   */
  async insertReceipt(receipt: ReceiptVm): Promise<Receipt> {
    const url = endpoints.receipts.insert
    
    // Clean payload - remove undefined values
    const cleanPayload = Object.fromEntries(
      Object.entries(receipt).filter(([_, value]) => value !== undefined)
    ) as ReceiptVm
    
    console.group('📤 ReceiptsService.insertReceipt')
    console.log('Endpoint:', url)
    console.table(cleanPayload)
    console.log('Payload JSON:', JSON.stringify(cleanPayload, null, 2))
    console.log('Invoice IDs:', cleanPayload.invoiceIds)
    console.groupEnd()
    
    const response = await apiClient.post<ApiResponse<Receipt> | Receipt>(url, cleanPayload)
    
    console.group('📥 ReceiptsService.insertReceipt - Response')
    console.log('Status:', response.status, response.statusText)
    console.log('Response Data:', JSON.stringify(response.data, null, 2))
    console.groupEnd()
    
    if ('data' in response.data && 'success' in response.data) {
      return this.mapReceiptData((response.data as ApiResponse<Receipt>).data)
    }
    
    return this.mapReceiptData(response.data as Receipt)
  }

  /**
   * Update receipt
   */
  async updateReceipt(receipt: ReceiptVm): Promise<Receipt> {
    const url = endpoints.receipts.update
    
    console.log('=== ReceiptsService.updateReceipt ===')
    console.log('Endpoint:', url)
    console.log('Payload:', JSON.stringify(receipt, null, 2))
    console.log('=====================================')
    
    const response = await apiClient.put<ApiResponse<Receipt> | Receipt>(url, receipt)
    
    console.log('=== API Response ===')
    console.log('Response Status:', response.status)
    console.log('Response Data:', JSON.stringify(response.data, null, 2))
    console.log('====================')
    
    if ('data' in response.data && 'success' in response.data) {
      return this.mapReceiptData((response.data as ApiResponse<Receipt>).data)
    }
    
    return this.mapReceiptData(response.data as Receipt)
  }

  /**
   * Map API response to Receipt interface
   */
  private mapReceiptData(receiptData: any): Receipt {
    return {
      receiptId: receiptData.receiptId || receiptData.id || 0,
      receiptNo: receiptData.receiptNo || receiptData.receiptNumber || '',
      receiptDate: receiptData.receiptDate || '',
      invoiceId: receiptData.invoiceId || 0,
      invoiceNo: receiptData.invoiceNo || receiptData.invoiceNumber || '',
      customerId: receiptData.customerId || 0,
      customerName: receiptData.customerName || receiptData.customerNameEn || receiptData.customerNameAr || '',
      customerNameEn: receiptData.customerNameEn || receiptData.customerName || '',
      customerNameAr: receiptData.customerNameAr || '',
      refNo: receiptData.refNo || receiptData.refNumber || '',
      paymentType: receiptData.paymentType || '',
      amount: receiptData.amount || 0,
      advance: receiptData.advance || 0,
      recipientAccount: receiptData.recipientAccount || receiptData.recipientAccountId || 0,
      recipientAccountName: receiptData.recipientAccountName || '',
      note: receiptData.note || '',
      advanceId: receiptData.advanceId || 0,
      branchId: receiptData.branchId || 0,
      ipAddress: receiptData.ipAddress || '',
    } as Receipt
  }
}

export const receiptsService = new ReceiptsService()

